import { useFormik } from "formik";
import React, { useState, useEffect } from "react";
import BackButton from "../../component/BackButton";
import * as Yup from "yup";
import { addTermsConditions } from "../../api/termsConditions";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router";
import { FetchTermsConditionsDetail } from "./termsConditionsSlice";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../component/Loader";
import Select from "react-select";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Link,
  List,
  Alignment,
  Font,
  Heading,
  Table,
  TableToolbar,
  Image,
  ImageToolbar,
  ImageResize,
  ImageUpload
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';

// Custom upload adapter for CKEditor - converts images to base64 data URLs
class MyUploadAdapter {
  constructor(loader) {
    this.loader = loader;
  }

  async upload() {
    const file = await this.loader.file;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve({ default: reader.result });
      };
      reader.onerror = error => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  }

  abort() {
    // Abort upload if needed
  }
}

function MyCustomUploadAdapterPlugin(editor) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
    return new MyUploadAdapter(loader);
  };
}

function AddTermsConditions({
  tcType = "admin",
  listPath = "/terms-and-conditions",
}) {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authState = useSelector((state) => state.auth);
  const isPlatformSuperAdmin =
    authState?.user_type?.toLowerCase() === "super_admin";
  const showCompanySelect = tcType === "company" && isPlatformSuperAdmin && !id;

  const detail = useSelector((state) => state.termsConditions.detail);
  const [submitting, setSubmitting] = useState(false);
  const [contentMode, setContentMode] = useState("editor");
  const [selectedPdf, setSelectedPdf] = useState(null);

  let companyOptions = [];
  const companiesStr = localStorage.getItem("companies");
  if (companiesStr) {
    try {
      const companies = JSON.parse(companiesStr);
      if (Array.isArray(companies) && companies.length > 0) {
        companyOptions = companies.map((c) => ({
          value: c.id,
          label: c.company_name,
        }));
      }
    } catch (e) {
      companyOptions = [];
    }
  }

  useEffect(() => {
    if (id) {
      dispatch(FetchTermsConditionsDetail(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (detail) {
      setContentMode(detail.pdf ? "pdf" : "editor");
    }
  }, [detail]);

  const formik = useFormik({
    initialValues: {
      version: detail?.version || "",
      content: detail?.content || "",
      company_id: detail?.company_id || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      version: Yup.string().required("Required"),
      company_id: showCompanySelect
        ? Yup.number().required("Required")
        : Yup.mixed().notRequired(),
    }),
    onSubmit: (values, { setFieldError }) => {
      if (contentMode === "editor" && !values.content?.trim()) {
        setFieldError("content", "Required");
        return;
      }
      if (contentMode === "pdf" && !selectedPdf && !detail?.pdf) {
        setFieldError("pdf", "Required");
        return;
      }
      setSubmitting(true);
      const payload = {
        version: values.version,
        content: contentMode === "editor" ? values.content : "",
        tc_type: tcType,
      };
      if (contentMode === "pdf" && selectedPdf) {
        payload.pdf = selectedPdf;
      }
      if (showCompanySelect && values.company_id) {
        payload.company_id = values.company_id;
      }
      if (id) {
        payload.terms_conditions_id = id;
      }
      addTermsConditions(payload).then((res) => {
        setSubmitting(false);
        if (res?.code === 1) {
          navigate(listPath);
          toast.success(res?.message, { autoClose: false, closeButton: true });
        } else {
          toast.error(res?.message);
        }
      });
    },
  });

  return (
    <article className="col-lg-9">
      <div className="article-body store-bd-gy">
        <BackButton />
        <div className="pt-3 px-3 rad">
          <div className="row align-items-center col-rever">
            <div className="col-sm-12 col-md-6 col-lg-6 mb-3">
              <h4>
                {id ? "Edit Terms and Conditions" : "Add Terms and Conditions"}
              </h4>
            </div>
          </div>
        </div>

        <div className="row mt-1">
          <div className="col-sm-12">
            <form className="form-style" onSubmit={formik.handleSubmit}>
              <div className="row">
                {showCompanySelect && companyOptions.length > 0 && (
                  <div className="col-sm-12 col-md-12 mb-4">
                    <div className="form-group">
                      <label className="fw-bold">Company</label>
                      {formik.errors?.company_id &&
                        formik.touched?.company_id && (
                          <span className="text-red ms-1">
                            {formik.errors?.company_id}
                          </span>
                        )}
                      <Select
                        className="form-control"
                        options={companyOptions}
                        isSearchable
                        name="company_id"
                        placeholder="Select a company..."
                        value={companyOptions.find(
                          (option) => option.value == formik.values.company_id
                        )}
                        onChange={(option) =>
                          formik.setFieldValue(
                            "company_id",
                            option ? Number(option.value) : ""
                          )
                        }
                        onBlur={() =>
                          formik.setFieldTouched("company_id", true)
                        }
                      />
                    </div>
                  </div>
                )}

                <div className="col-sm-12 col-md-6 col-lg-6 mb-4">
                  <div className="form-group">
                    <label className="fw-bold">Version</label>
                    {formik.errors?.version && formik.touched?.version && (
                      <span className="text-red ms-1">
                        {formik.errors?.version}
                      </span>
                    )}
                    <input
                      type="text"
                      className="form-control"
                      name="version"
                      placeholder="e.g. 1.0, 2.0"
                      value={formik.values.version}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                  </div>
                </div>

                <div className="col-sm-12 col-md-6 col-lg-6 mb-4">
                  <div className="form-group">
                    <label className="fw-bold">Content Type</label>
                    <div className="d-flex gap-3 mt-2">
                      <label className="d-flex align-items-center gap-2">
                        <input
                          type="radio"
                          name="contentMode"
                          value="editor"
                          checked={contentMode === "editor"}
                          onChange={() => {
                            setContentMode("editor");
                            setSelectedPdf(null);
                          }}
                        />
                        Editor
                      </label>
                      <label className="d-flex align-items-center gap-2">
                        <input
                          type="radio"
                          name="contentMode"
                          value="pdf"
                          checked={contentMode === "pdf"}
                          onChange={() => {
                            setContentMode("pdf");
                            formik.setFieldValue("content", "");
                          }}
                        />
                        Upload PDF
                      </label>
                    </div>
                  </div>
                </div>

                {contentMode === "pdf" ? (
                  <div className="col-sm-12 col-md-12 col-lg-12 mb-4">
                    <div className="form-group">
                      <label className="fw-bold">PDF File</label>
                      {formik.errors?.pdf && (
                        <span className="text-red ms-1">
                          {formik.errors?.pdf}
                        </span>
                      )}
                      <div className="input-container">
                        <input
                          type="file"
                          accept="application/pdf"
                          name="pdf"
                          onChange={(e) => {
                            if (e.target.files.length > 0) {
                              setSelectedPdf(e.target.files[0]);
                            }
                          }}
                        />
                      </div>
                      {(selectedPdf || detail?.pdf) && (
                        <div className="d-flex align-items-center gap-3 mt-2">
                          <img src="assets/images/pdf-icon.png" alt="file" />
                          {selectedPdf ? (
                            <span>{selectedPdf.name}</span>
                          ) : (
                            <a
                              href={detail.pdf}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View existing PDF
                            </a>
                          )}
                          {selectedPdf && (
                            <img
                              src="assets/images/delete-sm.png"
                              alt="remove"
                              className="pdfCrossIcon cursor-pointer"
                              onClick={() => setSelectedPdf(null)}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="col-sm-12 col-md-12 col-lg-12 mb-4">
                    <div className="form-group">
                      <label className="fw-bold">Terms and Conditions</label>
                      {formik.errors?.content && formik.touched?.content && (
                        <span className="text-red ms-1">
                          {formik.errors?.content}
                        </span>
                      )}
                      <div className="input-container">
                        <CKEditor
                          editor={ClassicEditor}
                          config={{
                            licenseKey: 'GPL',
                            plugins: [
                              Essentials,
                              Paragraph,
                              Bold,
                              Italic,
                              Underline,
                              Link,
                              List,
                              Alignment,
                              Font,
                              Heading,
                              Table,
                              TableToolbar,
                              Image,
                              ImageToolbar,
                              ImageResize,
                              ImageUpload,
                              MyCustomUploadAdapterPlugin
                            ],
                            toolbar: [
                              'undo', 'redo', '|',
                              'heading', '|',
                              'bold', 'italic', 'underline', '|',
                              'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor', '|',
                              'alignment:left', 'alignment:center', 'alignment:right', 'alignment:justify', '|',
                              'link', 'uploadImage', 'insertTable', '|',
                              'bulletedList', 'numberedList', 'outdent', 'indent', '|',
                              'removeFormat'
                            ],
                            heading: {
                              options: [
                                { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                                { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                                { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                                { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                              ]
                            },
                            fontSize: {
                              options: [
                                10, 12, 14, 'default', 18, 20, 24, 28, 32, 36, 48
                              ]
                            },
                            fontFamily: {
                              options: [
                                'default',
                                'Arial, Helvetica, sans-serif',
                                'Courier New, Courier, monospace',
                                'Georgia, serif',
                                'Lucida Sans Unicode, Lucida Grande, sans-serif',
                                'Tahoma, Geneva, sans-serif',
                                'Times New Roman, Times, serif',
                                'Trebuchet MS, Helvetica, sans-serif',
                                'Verdana, Geneva, sans-serif'
                              ]
                            },
                            image: {
                              toolbar: [
                                'imageTextAlternative', '|',
                                'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', 'imageStyle:side', '|',
                                'resizeImage', '|',
                                'toggleImageCaption', '|',
                                'imageRemove'
                              ],
                              styles: [
                                'inline',
                                'alignLeft',
                                'alignCenter',
                                'alignRight',
                                'wrapText',
                                'breakText',
                                'side'
                              ],
                              resizeUnit: 'px'
                            },
                            table: {
                              contentToolbar: [
                                'tableColumn', 'tableRow', 'mergeTableCells'
                              ]
                            }
                          }}
                          data={formik.values.content}
                          onReady={(editor) => {
                            editor.editing.view.change((writer) => {
                              writer.setStyle(
                                "min-height",
                                "400px",
                                editor.editing.view.document.getRoot()
                              );
                            });
                          }}
                          onChange={(event, editor) => {
                            formik.setFieldValue("content", editor.getData());
                          }}
                          onBlur={() =>
                            formik.setFieldTouched("content", true)
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="col-sm-12 col-md-12 text-center">
                  <button
                    type="submit"
                    className="btn btn-save"
                    disabled={submitting}
                  >
                    {submitting ? <Loader /> : "Done"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </article>
  );
}

export default AddTermsConditions;
